import { useRxData } from 'rxdb-hooks';
import { escapeRegExp, getMonthNumber } from '@helper/utils';
import { RxCollection } from 'rxdb';
import { useCallback } from 'react';
import { MeetingsFilter, MeetingsObject } from '..';
import { useDebounceSearch } from '@hooks/useDebounceSearch';
import { IMeetingReturn, MeetingFilterObject } from '.';
import { useFilterStore } from '@store/filter';
import { useRxCountQuery } from '@hooks/useCount';
import { useUserRoleType } from '@hooks/useUserRoleType';

export const GetMeetings = (filter: MeetingFilterObject): IMeetingReturn => {
	const { search } = useFilterStore();
	const debouncedSearch = useDebounceSearch(search);
	const userRole = useUserRoleType();

	const collectionName = userRole === 'RBDM' ? 'meeting_list_rbdm' : 'meeting_list';

	const queryConstructor = useCallback(
		(collection: RxCollection<MeetingsObject>) => {
			const selector: MeetingsFilter = {};

			const actualCollectionName = collection.name;

			if (debouncedSearch && debouncedSearch.trim() !== '') {
				selector.name = { $regex: `.*${escapeRegExp(debouncedSearch)}.*`, $options: 'i' };
			}

			if (filter.type && filter.type !== 'FCC Meeting') {
				selector.status = { $eq: filter.type };
			}
			const [startDate, endDate] = filter.filter_date || [];
			const meetingStatus = Array.isArray(filter.meetingStatus) ? filter.meetingStatus?.map((item) => item.value) : [];
			const clubData = filter.club?.map((item) => item.value) || [];

			if (startDate && endDate) {
				selector.startdate = {
					$gte: startDate.toISOString(),
					$lte: endDate.toISOString(),
				};
			}

			if (meetingStatus.length > 0) {
				selector.status = { $in: meetingStatus };
			}

			if (clubData.length > 0) {
				selector.club_name_c = {
					$regex: `.*${escapeRegExp(clubData.join(','))}.*`,
					$options: 'i',
				};
			}

			if (filter.month) {
				selector.startdate = {
					...selector.startdate,
					$regex: `-${getMonthNumber(filter.month)}-`,
				};
			}

			if (filter.meetingType !== null && filter.meetingType !== undefined && filter.meetingType !== '') {
				selector.meeting_type_c = { $eq: filter.meetingType };
			}
			if (filter.meetingTheme) {
				selector.meeting_theme_c = { $eq: filter.meetingTheme };
			}

			const isActualRBDMCollection = actualCollectionName === 'meeting_list_rbdm';

			if (
				filter.mdicode &&
				filter.mdicode.trim() !== '' &&
				isActualRBDMCollection &&
				userRole === 'RBDM' &&
				userRole.trim() !== ''
			) {
				selector.mdicode_c = { $eq: filter.mdicode };
			}

			// For FCC meetings, only show meetings where rbdm_code is not null
			if (filter.type === 'FCC Meeting') {
				selector.rbdm_code = { $exists: true, $ne: null };

				// If a specific rbdm_code is provided in filter, use that instead
				if (filter.rbdm_code && filter.rbdm_code.trim() !== '') {
					selector.rbdm_code = { $eq: filter.rbdm_code };
				}
			}

			(Object.keys(selector) as Array<keyof MeetingsFilter>).forEach((key) => {
				if (selector[key] === undefined || selector[key] === null) {
					delete selector[key];
				}
			});

			try {
				return collection
					.find({
						selector: Object.keys(selector).length > 0 ? selector : {},
					})
					.sort({ startdate: 'desc' });
			} catch (error) {
				if (error instanceof Error && (error.message?.includes('mdicode_c') || error.message?.includes('QU13'))) {
					delete selector.mdicode_c;
					return collection
						.find({
							selector: Object.keys(selector).length > 0 ? selector : {},
						})
						.sort({ startdate: 'desc' });
				}
				throw error;
			}
		},
		[filter, debouncedSearch, userRole]
	);

	const totalCount = useRxCountQuery<MeetingsObject>(collectionName, queryConstructor);

	const { result, isFetching, fetchMore } = useRxData<MeetingsObject>(collectionName, queryConstructor, {
		pageSize: 20,
		pagination: 'Infinite',
	});

	return { result, isFetching, fetchMore, totalCount };
};
